import React from 'react';
import { createPortal } from 'react-dom';
import { Participant } from '../types';
import Avatar from './Avatar';
import {
  XIcon,
  DownloadIcon,
  MapPinIcon,
  BriefcaseIcon,
  UserIcon,
  MailIcon,
  BarChartIcon,
  CheckSquareIcon,
  ClockIcon,
  UsersIcon
} from './icons';

interface ParticipantProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: Participant;
  email?: string;
  groups?: string[];
  metrics?: {
    submissionCount: number;
    completionRate: number;
    lastActive?: string;
  };
}

const ParticipantProfileModal: React.FC<ParticipantProfileModalProps> = ({
  isOpen,
  onClose,
  participant,
  email,
  groups = [],
  metrics
}) => {
  if (!isOpen) return null;

  const handleDownload = async () => {
    try {
      const imageUrl = participant.avatarUrl;
      if (!imageUrl) {
        alert('No profile picture available to download.');
        return;
      }

      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${participant.name.replace(/\s+/g, '_')}_profile.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

    } catch (error) {
      console.error('Download failed:', error);
      if (participant.avatarUrl) {
        window.open(participant.avatarUrl, '_blank');
      }
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-app-border shrink-0">
          <h3 className="font-bold text-text-ink text-lg">Participant Profile</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-full text-text-faint transition-colors"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6">
          <div className="flex flex-col items-center mb-8">
            {/* Large Avatar */}
            <div className="relative mb-4 group">
              <Avatar
                name={participant.name}
                avatarUrl={participant.avatarUrl}
                id={participant.id}
                className="w-28 h-28 rounded-full border-4 border-white shadow-lg object-cover"
              />
              <button
                onClick={handleDownload}
                className="absolute bottom-0 right-0 bg-action/10 text-action p-2 rounded-full shadow-md hover:bg-action/15 transition-colors group-hover:scale-110"
                title="Download Profile Picture"
              >
                <DownloadIcon className="w-4 h-4" />
              </button>
            </div>

            <h2 className="text-2xl font-bold text-text-ink mb-1 text-center">{participant.name}</h2>

            {(email || participant.email) && (
              <div className="flex items-center justify-center gap-1.5 text-text-muted mb-3 text-sm">
                <MailIcon className="w-4 h-4 opacity-70" />
                {email || participant.email}
              </div>
            )}

            {/* Status */}
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-2 text-sm bg-app-bg px-3 py-1 rounded-full border border-app-border">
                <span className={`w-2 h-2 rounded-full ${participant.status === 'active' ? 'bg-status-ok' : 'bg-neutral-300'}`}></span>
                <span className="text-text-muted font-medium capitalize">{participant.status}</span>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          {metrics && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-action-soft p-3 rounded-xl border border-action-mid flex flex-col items-center text-center">
                <div className="mb-1 text-action"><BarChartIcon className="w-5 h-5" /></div>
                <span className="text-xl font-bold text-text-ink">{metrics.submissionCount}</span>
                <span className="text-xs text-text-muted font-medium">Submissions</span>
              </div>
              <div className="bg-chip-photo-bg p-3 rounded-xl border border-chip-photo flex flex-col items-center text-center">
                <div className="mb-1 text-chip-photo"><CheckSquareIcon className="w-5 h-5" /></div>
                <span className="text-xl font-bold text-text-ink">{Math.round(metrics.completionRate)}%</span>
                <span className="text-xs text-text-muted font-medium">Completion</span>
              </div>
              <div className="bg-status-ok-bg p-3 rounded-xl border border-status-ok flex flex-col items-center text-center">
                <div className="mb-1 text-status-ok"><ClockIcon className="w-5 h-5" /></div>
                <span className="text-sm font-bold text-text-ink mt-1 line-clamp-1">{metrics.lastActive || 'Never'}</span>
                <span className="text-xs text-text-muted font-medium">Last Active</span>
              </div>
            </div>
          )}

          {/* Groups */}
          {groups.length > 0 && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="p-1.5 rounded-full bg-action text-white flex items-center justify-center">
                  <UsersIcon className="w-3.5 h-3.5" />
                </div>
                <span>Groups</span>
              </h4>
              <div className="flex flex-wrap gap-2">
                {groups.map((group, idx) => (
                  <span key={idx} className="bg-white border border-app-border text-text-ink px-3 py-1 rounded-lg text-sm font-medium shadow-sm">
                    {group}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Details Grid */}
          <div>
            <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
              <UserIcon className="w-4 h-4" /> Personal Details
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-app-bg rounded-xl border border-app-border">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-lg text-action shadow-sm"><MapPinIcon className="w-4 h-4" /></div>
                  <span className="text-sm text-text-muted font-medium">Location</span>
                </div>
                <span className="text-sm text-text-ink font-semibold">{participant.location || 'N/A'}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-app-bg rounded-xl border border-app-border">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-lg text-chip-photo shadow-sm"><BriefcaseIcon className="w-4 h-4" /></div>
                  <span className="text-sm text-text-muted font-medium">Occupation</span>
                </div>
                <span className="text-sm text-text-ink font-semibold">{participant.occupation || 'N/A'}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-app-bg rounded-xl border border-app-border">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-lg text-action shadow-sm"><UserIcon className="w-4 h-4" /></div>
                  <span className="text-sm text-text-muted font-medium">Age & Gender</span>
                </div>
                <span className="text-sm text-text-ink font-semibold">
                  {participant.age ? `${participant.age}` : 'N/A'} • {participant.gender || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ParticipantProfileModal;